import {ViewController} from "data/types/structure";
import {ConnextraType, createConnextraScriptTag} from "data/utils/connextra";
import {inject, injectable} from "inversify";
import {action, computed, makeAutoObservable, observable, reaction, when} from "mobx";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RequestState, ShareType, SocialNetwork} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IGameplayStore} from "data/stores/gameplay/gameplay.store";
import {find, includes, isEmpty, isEqual, isNumber, uniqBy} from "lodash";
import {type IStepper, Stepper} from "data/utils/stepper/stepper.utility";
import type {IUserStore} from "data/stores/user/user.store";
import {Bindings} from "data/constants/bindings";
import {trackSentryErrors} from "data/utils";
import {IAnswer, IContest, IQuestionWithAnswers} from "data/types/contests";
import {QuestionUtils} from "data/utils/question_utils";
import {AxiosError} from "axios";
import {ContestUtils} from "data/utils/contest_utils";
import {ShareUtility} from "data/utils/social_share/social_share.utility";
import {TRIALFIRE_TEAM} from "data/constants";
import dayjs from "dayjs";

interface IControllerProps {
	contestID: number;
}

export interface IContestController extends ViewController<IControllerProps> {
	get i18n(): ILocalizationStore;

	get requestState(): RequestState;

	get allContests(): IContest[];

	get activeContests(): IContest[];

	get currentContest(): IContest | null;

	get contestQuestions(): IQuestionWithAnswers[] | undefined;
	get contestCorrectAnswers(): IAnswer[];
	get answersPoints(): number;

	get isLoading(): boolean;

	get isQuestionsLoading(): boolean;

	shareTw: () => void;
	shareFB: () => void;
	shareViaMobile: () => void;
	shareModal: (contestId: number) => void;

	updateContestID: (contestID: number) => void;
	setContestComplete: (contestID: number) => void;
	stepper: IStepper;
	onCompleteClick: () => void;
	handleQuestionAnswer: (questionId: number, optionId: number) => void;
}

@injectable()
export class ContestController implements IContestController {
	@observable private _contestID!: number;
	@observable private _requestState: RequestState = RequestState.IDLE;
	@observable public stepper = new Stepper();
	private _questionsReactionDisposer!: ReturnType<typeof when>;
	@observable _answeredQuestions: {question_id: number; answer_id: number}[] = [];

	get requestState() {
		return this._requestState;
	}

	get allContests() {
		return this._gameplayStore.allContests;
	}

	get activeContests() {
		return this._gameplayStore.actualContests;
	}

	get isLoading() {
		return isEqual(this._requestState, RequestState.PENDING);
	}

	get isQuestionsLoading() {
		return this.isLoading && isEmpty(this.contestQuestions);
	}

	@computed get contestQuestions(): IQuestionWithAnswers[] {
		return this._gameplayStore.getQuestionsByContestID(this._contestID);
	}

	@computed get contestCorrectAnswers() {
		return this._gameplayStore.getCorrectAnswersByContestID(this._contestID);
	}

	@computed get answersPoints() {
		const correctQuestionsIds = this.contestCorrectAnswers.map((it) => it.questionId);
		const correctQuestions = this.contestQuestions.filter((q) =>
			includes(correctQuestionsIds, q.id)
		);

		return ContestUtils.getTotalPoints(correctQuestions) || 0;
	}

	@computed get currentContest() {
		return find(this._gameplayStore.allContests, {id: this._contestID}) || null;
	}

	constructor(
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.GameplayStore) private readonly _gameplayStore: IGameplayStore,
		@inject(Bindings.UserStore) private readonly _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get _shareMessage() {
		return this.i18n.t("share.twitter.contest_text", {
			contest_name: this.i18n.findValueByLocaleObject(this.currentContest?.name ?? {}),
			X: this.i18n.findValueByLocaleObject(this.currentContest?.name ?? {}),
		});
	}

	shareTw = () => {
		if (this._contestID) {
			ShareUtility.share({
				message: this._shareMessage,
				type: ShareType.CONTEST,
				contestId: this._contestID,
				socialNetwork: SocialNetwork.TWITTER,
				locale: this.i18n.locale,
			});
		}
	};

	shareFB = () => {
		if (this._contestID) {
			ShareUtility.share({
				message: this._shareMessage,
				type: ShareType.CONTEST,
				contestId: this._contestID,
				socialNetwork: SocialNetwork.FACEBOOK,
				locale: this.i18n.locale,
			});
		}
	};

	shareViaMobile = () => {
		if (this._contestID) {
			ShareUtility.share({
				message: this._shareMessage,
				type: ShareType.CONTEST,
				contestId: this._contestID,
				socialNetwork: SocialNetwork.MOBILE,
				locale: this.i18n.locale,
			});
		}
	};

	shareModal = () => {
		if (this._contestID) {
			this._modalsStore.showModal(ModalType.SHARE_MODAL, {
				message: "",
				contestId: this._contestID,
			});
		}
	};

	@action public handleQuestionAnswer = (questionID: number, optionId: number) => {
		const question = find(this.contestQuestions, {id: questionID});

		if (!question || !QuestionUtils.isScheduled(question)) {
			return;
		}

		this._gameplayStore
			.saveAnswers({
				contestID: this._contestID,
				questionId: questionID,
				optionId,
				locale: this.i18n.languageOnly,
			})
			.then(() => {
				question.answers = [
					{
						isCorrect: null,
						questionId: questionID,
						optionId: optionId,
					},
				];

				this._answeredQuestions.push({question_id: questionID, answer_id: optionId});
			})
			.catch(this.onError);
	};

	@action updateContestID = async (contestID: number) => {
		await this.requestData(contestID);
		this._contestID = contestID;
	};

	@action setContestComplete = (contestID: number) => {
		this._gameplayStore.setContestComplete(contestID);
	};

	@action
	private async requestData(contestId: number) {
		this._requestState = RequestState.PENDING;

		if (isEmpty(this.allContests)) {
			await this._gameplayStore.requestContestsSafety();
		}

		try {
			if (contestId) {
				await this._gameplayStore.requestData({
					locale: this.i18n.languageOnly,
					contestId: contestId,
				});
			}

			this.onSuccess();
		} catch (e) {
			this.onError(e as AxiosError);
		}
	}

	@action onError = (error: AxiosError) => {
		trackSentryErrors(error, {}, "contest - error handler");
		this._requestState = RequestState.ERROR;
		this._modalsStore.showModal(ModalType.ERROR, {
			message: error.message,
			errors: error.response?.data,
		});
	};

	@action onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	private static checkIsValidWidgetParams(questionId: number, optionId: number): boolean {
		return isNumber(questionId) && isNumber(optionId);
	}

	@action init({contestID}: IControllerProps) {
		this._contestID = contestID;

		this._questionsReactionDisposer = reaction(
			() => this.contestQuestions.length,
			(questionsCount) => {
				this.stepper = new Stepper(questionsCount);

				const params = new URLSearchParams(window.location.search);
				const questionId = Number(params.get("questionId"));
				const optionId = Number(params.get("optionId"));

				if (ContestController.checkIsValidWidgetParams(questionId, optionId)) {
					this.handleQuestionAnswer(questionId, optionId);
				}
			},
			{fireImmediately: true}
		);
	}

	dispose(): void {
		this._questionsReactionDisposer();
	}

	onCompleteClick = () => {
		createConnextraScriptTag(ConnextraType.PICKS_CONFIRM);

		const trackPayload = {
			game: "team_blitz_picks",
			team: TRIALFIRE_TEAM,
			season: dayjs(this.currentContest?.dateStart || Date.now()).format("YYYY"),
			week: this.currentContest?.week ? this.currentContest.week : 0,
			response: uniqBy(this._answeredQuestions, "question_id"),
		};

		console.log("Trialfire----", trackPayload);

		window.Trialfire?.track("pick_submitted", JSON.stringify(trackPayload));
	};
}
